import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const UPDATED = "June 1, 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-semibold text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-gray-400">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-200" data-testid="terms-page">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="terms-home-link" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-6 w-6 object-contain" /> StreamWorship
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30">
            <FileText className="h-6 w-6 text-amber-400" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white">Terms of Service</h1>
            <p className="text-xs text-gray-500 mt-1">Last updated: {UPDATED}</p>
          </div>
        </div>

        <p className="mt-8 text-sm leading-relaxed text-gray-400">
          These Terms of Service ("Terms") govern your access to and use of the StreamWorship platform and
          the <span className="text-gray-300">Ascend TV App</span> (together, the "Service"). By creating an
          account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
        </p>

        <Section title="1. The Service">
          <p>
            StreamWorship provides live streaming, video hosting, playlists, embeds, and analytics for
            churches and organizations. The Ascend TV App is a companion viewer application that lets your
            audience browse channels and watch published live streams and video libraries.
          </p>
        </Section>

        <Section title="2. Accounts and eligibility">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>You must provide accurate information and keep your account credentials secure.</li>
            <li>You are responsible for all activity that occurs under your account and sub-accounts.</li>
            <li>New organization accounts may start as pending and require administrator approval before activation.</li>
            <li>You must be authorized to act on behalf of the organization you register.</li>
          </ul>
        </Section>

        <Section title="3. Acceptable use">
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Upload or stream content that is unlawful, infringing, or that you do not have the rights to distribute.</li>
            <li>Violate the privacy or intellectual-property rights of others.</li>
            <li>Distribute malware, attempt to breach security, or disrupt the Service.</li>
            <li>Resell or misrepresent the Service without our written permission.</li>
          </ul>
        </Section>

        <Section title="4. Your content and responsibilities">
          <p>
            You retain ownership of the video and other content you upload or stream ("Your Content"). You
            grant us a limited license to host, process, transcode, and deliver Your Content solely to
            operate the Service and display it in your embeds and in the Ascend TV App as you configure.
            You are responsible for obtaining any consents needed to broadcast people featured in Your Content.
          </p>
        </Section>

        <Section title="5. Subscriptions, billing, and trials">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Paid plans are billed on a recurring basis through our payment processor until cancelled.</li>
            <li>Free trials convert to a paid plan only when you choose a plan; you can cancel before then.</li>
            <li>Except where required by law, payments are non-refundable for periods already served.</li>
            <li>Plan features, storage quotas, and pricing may change with reasonable notice.</li>
          </ul>
        </Section>

        <Section title="6. Third-party services">
          <p>
            The Service relies on third-party providers (for example, cloud storage, streaming
            infrastructure, payment processing, and email delivery). Your use of the Service may also be
            subject to those providers' terms. We are not responsible for third-party services outside our
            control.
          </p>
        </Section>

        <Section title="7. The Ascend TV App">
          <p>
            Content shown in the Ascend TV App is controlled by each church through their StreamWorship
            account, including which streams and videos are published. We may curate the channel directory
            for reliability and quality. The Ascend TV App is provided as part of the Service and is subject
            to these Terms.
          </p>
        </Section>

        <Section title="8. Disclaimers">
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, whether
            express or implied. We do not warrant that the Service will be uninterrupted, error-free, or
            free of harmful components. Live streaming depends on your own encoder settings and internet
            connection.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, StreamWorship and its affiliates will not be liable for
            any indirect, incidental, special, or consequential damages, or for lost profits or data,
            arising from your use of the Service. Our total liability for any claim will not exceed the
            amount you paid us for the Service in the twelve months before the claim.
          </p>
        </Section>

        <Section title="10. Termination">
          <p>
            You may stop using the Service at any time. We may suspend or terminate access if you violate
            these Terms or use the Service in a way that risks harm to others or to the platform. Upon
            termination, your right to use the Service ends and Your Content may be deleted after a
            reasonable period.
          </p>
        </Section>

        <Section title="11. Changes to these Terms">
          <p>
            We may update these Terms from time to time. When we make material changes we will update the
            "Last updated" date above and, where appropriate, notify you. Continued use of the Service after
            changes take effect means you accept the updated Terms.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions about these Terms? Reach us at{" "}
            <a href="mailto:support@streamworship.org" data-testid="terms-contact-email" className="text-amber-400 hover:underline">support@streamworship.org</a>{" "}
            or through our <Link to="/contact" className="text-amber-400 hover:underline">contact page</Link>.
          </p>
        </Section>

        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-gray-600">
          © 2026 StreamWorship · streamworship.org · <Link to="/privacy" className="hover:text-gray-400 underline underline-offset-2">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}
